import { StoreListing, useStoreSettings } from '@portals/api/partners';
import { CurrencyCode, PeriodEnum, ProductPricingModel } from '@portals/types';
import { formatPrice } from '@portals/utils';

import { PaymentSettingsMapKeys } from '../types/store-listings.types';
import { mapStoreListingPricesToPaymentSettings } from '../utils/store-listings.utils';

const PERIOD_LABELS = {
  [PeriodEnum.OneTime]: '',
  [PeriodEnum.Monthly]: ' / month',
  [PeriodEnum.Yearly]: ' / year',
} as const;

export function useStoreListingPeriodPriceTitle() {
  const storeSettings = useStoreSettings();

  const getPriceTitle = (params: {
    currencyCode?: CurrencyCode;
    period: PaymentSettingsMapKeys;
    product?: StoreListing['product'];
    paymentSettings: ReturnType<typeof mapStoreListingPricesToPaymentSettings>;
  }) => {
    const currency =
      params.currencyCode || storeSettings.data?.default_currency;

    if (!currency) return 'No currency set';

    const price = params.paymentSettings[params.period]?.prices.find(
      (price) => price.currency === currency
    );

    if (!price) return 'No price set';

    const formattedPrice = formatPrice({
      value: price.amountInScu,
      currencyCode: price.currency,
      shouldConvertFromMinorToMajor: true,
    });

    let label: string;

    if (params.product?.pricing_model === ProductPricingModel.UsageBased) {
      label = ` / ${params.product.usage_based_unit_name}`;
    } else {
      label = PERIOD_LABELS[params.period as PeriodEnum];
    }

    return `${formattedPrice}${label}`;
  };

  return { getPriceTitle, isLoadingStoreSettings: storeSettings.isLoading };
}
